import { useCallback, useEffect, useRef } from 'react';
import { useBlocker } from 'react-router';
import type { BlockerFunction } from 'react-router';

interface UnsavedChangesGuardResult {
  // True while a navigation is being held back and the caller should be showing
  // its confirmation prompt.
  isLeaveBlocked: boolean;
  confirmLeave: () => Promise<void>;
  cancelLeave: () => void;
}

/**
 * Guards a page against losing unsaved work on the way out, covering both exits:
 * in-app navigation (React Router) and leaving the document entirely (refresh,
 * tab close, external link).
 *
 * `onBeforeLeave` runs — and is awaited — only on a confirmed in-app leave, so
 * it is the place to hang teardown that must happen before the page goes away,
 * such as releasing a server-side lock. A document unload can only be warned
 * about, never awaited, so `onBeforeLeave` is deliberately not run there.
 */
export function useUnsavedChangesGuard(
  hasUnsavedChanges: boolean,
  onBeforeLeave?: () => void | Promise<void>
): UnsavedChangesGuardResult {
  const shouldBlock = useCallback<BlockerFunction>(
    ({ currentLocation, nextLocation }) =>
      // Pathname only: the year selection lives in the query string, and pages
      // sync it back to the URL themselves. Blocking on the full location would
      // pop the prompt on those internal rewrites, which lose nothing.
      hasUnsavedChanges && currentLocation.pathname !== nextLocation.pathname,
    [hasUnsavedChanges]
  );

  const blocker = useBlocker(shouldBlock);

  // Set while a confirmed teardown is in flight. Cancelling in that window must
  // be ignored: the teardown has already started releasing the lock and cannot
  // be undone, and a `reset()` here would also make the trailing `proceed()`
  // an invalid blocker transition.
  const isConfirmingRef = useRef(false);

  useEffect(() => {
    if (!hasUnsavedChanges) return;
    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      // `preventDefault` is what current browsers act on for `beforeunload`.
      // The message itself is the browser's — pages cannot supply their own
      // text here.
      event.preventDefault();
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  const confirmLeave = useCallback(async (): Promise<void> => {
    isConfirmingRef.current = true;
    try {
      await onBeforeLeave?.();
    } catch (error) {
      // Callers wire this through a `Button`, which consumes handler rejections
      // without logging — this is the last place the error is seen, so leave a
      // trace before re-throwing for callers outside that contract.
      console.error('Teardown failed while leaving with unsaved changes', error);
      throw error;
    } finally {
      isConfirmingRef.current = false;
      // The user asked to leave and discard, so a failed teardown must not strand
      // them behind a prompt with no way forward — resume either way. Only while
      // the navigation is still held back, though: `proceed()` on a blocker that
      // has since been reset throws a router invariant.
      if (blocker.state === 'blocked') {
        blocker.proceed();
      }
    }
  }, [blocker, onBeforeLeave]);

  const cancelLeave = useCallback((): void => {
    if (isConfirmingRef.current) return;
    blocker.reset?.();
  }, [blocker]);

  return { isLeaveBlocked: blocker.state === 'blocked', confirmLeave, cancelLeave };
}
